// src/api/equipmentApi.js

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const equipmentApi = {
  /**
   * Get equipment with filters for a program
   */
  getEquipment: async (programId, filters = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams({
        programId,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        )
      }).toString();
      
      const url = `${API_URL}/api/sports-equipments?${queryParams}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      return data;
    } catch (error) {
      console.error('Error fetching equipment:', error);
      throw error;
    }
  },
  
  /**
   * Get single equipment by ID
   */
  getEquipmentById: async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/sports-equipments/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching equipment by ID:', error);
      throw error;
    }
  },
  
  /**
   * Get equipment by sport name (backward compatible)
   */
  getBySport: async (sportName) => {
    try {
      const response = await fetch(
        `${API_URL}/api/sports-equipments/sport/${encodeURIComponent(sportName)}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching equipment by sport:', error);
      throw error;
    }
  },
  
  /**
   * Track equipment view
   */
  trackView: async (equipmentId) => {
    try {
      await fetch(`${API_URL}/api/sports-equipments/${equipmentId}/track-view`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      // Silent fail - tracking shouldn't break UX
      console.warn('Failed to track view:', error);
    }
  },
  
  /**
   * Track purchase link click
   */
  trackClick: async (equipmentId, linkType, linkIndex = null) => {
    try {
      const body = { linkType };
      if (linkIndex !== null) {
        body.linkIndex = linkIndex;
      }
      
      await fetch(`${API_URL}/api/sports-equipments/${equipmentId}/track-click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
    } catch (error) {
      // Silent fail - tracking shouldn't break UX
      console.warn('Failed to track click:', error);
    }
  },
  
  /**
   * Get top-rated equipment
   */
  getTopRated: async (options = {}) => {
    try {
      const queryParams = new URLSearchParams(options).toString();
      const response = await fetch(
        `${API_URL}/api/sports-equipments/top-rated?${queryParams}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching top-rated equipment:', error);
      throw error;
    }
  },
  
  /**
   * Calculate stats from equipment list
   * (Client-side calculation for backward compatibility)
   */
  calculateStats: (equipments = [], assignments = []) => {
    // Ensure equipments is an array
    const equipmentList = Array.isArray(equipments) ? equipments : [];
    const totalEquipment = equipmentList.length;
    
    // Calculate average rating
    const equipmentWithRatings = equipmentList.filter(
      eq => eq.ratingSummary && eq.ratingSummary.totalReviews > 0
    );
    
    const averageRating = equipmentWithRatings.length > 0
      ? equipmentWithRatings.reduce((sum, eq) => sum + eq.ratingSummary.averageRating, 0) / equipmentWithRatings.length
      : 0;
    
    // Ensure assignments is an array
    const assignmentList = Array.isArray(assignments) ? assignments : [];
    
    // Calculate assignments (if provided)
    const totalAssigned = assignmentList.filter(a => a.status === 'active').length;
    const overdueCount = assignmentList.filter(a => {
      if (a.status !== 'active') return false;
      if (!a.dueDate) return false;
      return new Date(a.dueDate) < new Date();
    }).length;
    
    return {
      totalEquipment,
      averageRating,
      totalAssigned,
      overdueCount
    };
  }
};

export default equipmentApi;

